import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { Action } from '../../../typings/permissions';
import { userHasPermissionForProject, userHasPermissionForReleaseGroup } from '../../helpers/permissions';
import useTeamProjects from '../../hooks/api/teams/useTeamProjects';
import useTeamReleaseGroups from '../../hooks/api/teams/useTeamReleaseGroups';
import ProjectGroupModule from '../../redux/project_grouping';
import TeamsModule from '../../redux/teams';
import { hasError, selectOptionFactory } from '../../utils';
import Button, { ButtonGroup } from '../generic/Button';
import { ErrorAlert } from '../generic/ErrorAlert';
import Loader from '../generic/Loader';
import Select from '../generic/Select/Select';
import type { GetTeamResponse } from '../../../shared/types/routes/teams';
import type { SelectOption } from '../../types';

type AddTeamProject = {
  locator: string;
  title: string;
};

type AddTeamReleaseGroup = {
  id: number;
  title: string;
  releases: Array<{
    projects: Array<{
      projectId: string;
    }>;
  }>;
};

type Props = {
  team: GetTeamResponse;
  projects: AddTeamProject[];
  releaseGroups: AddTeamReleaseGroup[];
  onDone(projectIds: string[], releaseGroups: number[]): void;
  onHide(): void;
};

type OptionValue = {
  type: 'project';
  id: string;
} | {
  type: 'releaseGroup';
  id: number;
};

type SelectedOption = SelectOption<OptionValue>;

/**
 * The modal for adding project(s) to a team.
 *
 * @param {Team} team - The team we are adding project(s) to.
 * @param {Array<{ locator: string; title: string }>} projects - List of projects.
 * @param {(projectIds: string[], releaseGroups: number[]) => void} releaseGroups - List of release groups.
 * @param {releaseGroups: Array<{ id: number, title: string, releases: Array<{ projects: Array<{ projectId: string, }>, }>, }>} onDone - Method for saving the changes.
 * @param {() => void} onHide - Method for closing the modal/losing changes.
 */
const ModalAddTeamProjects = (props: Props) => {
  const dispatch = useDispatch();
  const status = useSelector(TeamsModule.selectors.getTeamUpdatingStatus);
  const fetchingAllProjectsInGroupStatus = useSelector(
    ProjectGroupModule.selectors.getFetchingAllProjectsInGroupStatus,
  );
  const fetchAllProjectsInReleaseGroup = useCallback(
    (groupId: number) => dispatch(ProjectGroupModule.actions.fetchAllProjectsInReleaseGroup(groupId)),
    [dispatch],
  );

  const { data: teamProjects, isLoading: isLoadingTeamProjects } = useTeamProjects(props.team.id, {
    skipPagination: true,
  });
  const { data: teamReleaseGroups, isLoading: isLoadingTeamReleaseGroups } = useTeamReleaseGroups(props.team.id, {
    skipPagination: true,
  });

  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);

  // checkedReleaseGroups will say for which release groups we've fetched all projects to be included among the
  // projects in the team.
  const [checkedReleaseGroups, setCheckedReleaseGroups] = useState<Set<number>>(new Set());

  const hasProjectBeenSelected = (project: AddTeamProject) => selectedOptions.some((selectedOption: SelectedOption) =>
    selectedOption.value.type === 'project' && project.locator === selectedOption.value.id);

  const hasReleaseGroupBeenSelected = (releaseGroup: AddTeamReleaseGroup) =>
    selectedOptions.some((selectedOption: SelectedOption) =>
      selectedOption.value.type === 'releaseGroup' && releaseGroup.id === selectedOption.value.id);

  const currentTeamProjects = useMemo(
    () => new Set<string>(teamProjects?.results?.map((teamProject) => teamProject.locator)),
    [teamProjects?.results],
  );
  const currentTeamReleaseGroups = useMemo(
    () => new Set<number>(teamReleaseGroups?.results?.map((teamReleaseGroup) => teamReleaseGroup.id)),
    [teamReleaseGroups?.results],
  );

  const isProjectInTeam = (project: AddTeamProject) => currentTeamProjects.has(project.locator);

  const isReleaseGroupInTeam = (releaseGroup: AddTeamReleaseGroup) => currentTeamReleaseGroups.has(releaseGroup.id);

  const shouldDisplayProject = (project: AddTeamProject) =>
    !isProjectInTeam(project) &&
    !hasProjectBeenSelected(project) &&
    userHasPermissionForProject(Action.Edit, project.locator);

  const shouldDisplayReleaseGroup = (releaseGroup: AddTeamReleaseGroup) =>
    !isReleaseGroupInTeam(releaseGroup) &&
    !hasReleaseGroupBeenSelected(releaseGroup) &&
    userHasPermissionForReleaseGroup(Action.Edit, releaseGroup.id);

  const projectOptions = props.projects
    .filter(shouldDisplayProject)
    .map(project => selectOptionFactory({ type: 'project', id: project.locator }, project.title));

  const releaseGroupOptions = props.releaseGroups
    .filter(shouldDisplayReleaseGroup)
    .map(releaseGroup => selectOptionFactory({ type: 'releaseGroup', id: releaseGroup.id }, releaseGroup.title));

  const modalOptions = [
    ...projectOptions,
    ...releaseGroupOptions,
  ];

  const filterProjectsAndReleaseGroups = (options: Array<{ label: string; value: OptionValue }>) => {
    const projectIds: string[] = [];
    const releaseGroupIds: number[] = [];

    options.forEach(({ value }) => {
      if (value.type === 'project') {
        projectIds.push(value.id);
      } else {
        releaseGroupIds.push(value.id);
      }
    });

    props.onDone(projectIds, releaseGroupIds);
  };

  const updateSelectedOptions = async (options: Array<{ label: string; value: OptionValue }>) => {
    const newCheckedReleaseGroups = new Set<number>();
    for (const { value } of options) {
      if (value.type === 'releaseGroup') {
        newCheckedReleaseGroups.add(value.id);
        if (!checkedReleaseGroups.has(value.id)) {
          // Add the projects of the release group into the options array.
          try {
            const resp = await fetchAllProjectsInReleaseGroup(value.id);
            resp.value?.data?.forEach((projectId: string) => {
              if (
                !options.some(opt => opt.value.type === 'project' && opt.value.id === projectId) &&
                !props.team.teamProjects?.some(teamProject => teamProject.projectId === projectId)
              ) {
                options.push({
                  label: projectId,
                  value: { type: 'project', id: projectId },
                });
              }
            });
          } catch (e) {
            // Error handled by Redux.
          }
        }
      }
    }
    setSelectedOptions(options);
    setCheckedReleaseGroups(newCheckedReleaseGroups);
  };

  const fetchingAllProjectsInGroup = fetchingAllProjectsInGroupStatus.loading;

  return (
    <Modal show onHide={props.onHide} className="modal-add-team-projects">
      <Modal.Header closeButton>
        <Modal.Title>
          Add Projects to {props.team.name}
          {fetchingAllProjectsInGroup && (
            <Loader
              size="20px"
              style={{ display: 'inline-block', width: '20px', height: '20px', verticalAlign: 'text-bottom', marginLeft: '5px' }} />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasError(status) && <ErrorAlert status={status} />}
        {hasError(fetchingAllProjectsInGroupStatus) && (
          <ErrorAlert status={fetchingAllProjectsInGroupStatus} />
        )}
        {status.loading || isLoadingTeamProjects || isLoadingTeamReleaseGroups
          ? (
            <Loader style={{ marginTop: '18px', marginBottom: '18px' }} />
            ) : (
              <Select
                isMulti
                name="projects"
                // @ts-expect-error It doesn't like the way we have two possible types for the value.
                options={modalOptions}
                onChange={updateSelectedOptions}
                value={selectedOptions}
              />
            )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="small"
            onClick={props.onHide}
            isDisabled={status.loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => filterProjectsAndReleaseGroups(selectedOptions)}
            isDisabled={status.loading || !selectedOptions.length}
          >
            Add
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddTeamProjects;
